import { promises as fs } from 'fs'
import path from 'path'

/*
Slightly modified and copied from https://github.com/erikjs14/next-build-cache
MIT License

Copyright (c)

Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all
copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
SOFTWARE.
 */

interface CachedData<T> {
  [key: string]: {
    data: T
    validUntil?: number
  }
}

interface CacheConfig {
  scheduleTtl: boolean
}

interface Cache {
  keys: () => Promise<string[]>
  put: <T>(key: string, val: T, ttl_ms?: number) => Promise<void>
  get: <T>(key: string) => Promise<T | null>
  scheduleTtl: (key: string, ttl: number) => void
}

export const stringHashCode = (str: string) => {
  let hash = 0
  str.split('').forEach((c) => {
    hash = Math.imul(31, hash) + c.charCodeAt(0)
  })

  // eslint-disable-next-line no-bitwise
  return String(hash | 0)
}

const cache = async (
  cacheFolderName: string,
  configParam: Partial<CacheConfig> = {},
): Promise<Cache> => {
  const read = fs.readFile
  const write = fs.writeFile
  let folderName = path.join(process.cwd(), cacheFolderName)
  try {
    if (!(await fs.stat(folderName)).isDirectory()) {
      console.error(`Cache directory '${folderName}' is a file and not a directory, using default`)
      folderName = path.join(process.cwd(), 'build_cache')
    }
  } catch (e: any) {
    if (e.code === 'ENOENT') {
      try {
        await fs.mkdir(folderName)
      } catch {
        // Do nothing if creation fails
      }
    }
  }
  const config = {
    scheduleTtl: true,
    ...configParam,
  }

  const scheduleTtl = (key: string, ttl: number) => {
    setTimeout(async () => {
      const data = JSON.parse(await read(path.join(folderName, stringHashCode(key)), { encoding: 'utf8' }))
      delete data[key]
      await write(path.join(folderName, stringHashCode(key)), JSON.stringify(data), { encoding: 'utf8' })
    }, ttl)
  }

  const keys = async (): Promise<string[]> => {
    let data: string[]
    try {
      data = await fs.readdir(folderName)
    } catch (error) {
      console.error('could not read keys')
      data = []
    }
    return data
  }

  const put = async <T>(key: string, val: T, ttl_ms?: number): Promise<void> => {
    const data = {
      [key]: {
        data: val,
        validUntil: ttl_ms ? new Date().getTime() + ttl_ms : undefined,
      },
    }
    await write(path.join(folderName, stringHashCode(key)), JSON.stringify(data), { encoding: 'utf8' })
    if (config.scheduleTtl && ttl_ms) scheduleTtl(key, ttl_ms)
  }

  const get = async <T>(key: string): Promise<T | null> => {
    let content: string = ''
    try {
      content = await read(path.join(folderName, stringHashCode(key)), { encoding: 'utf8' })
      await new Promise((resolve) => setTimeout(resolve, 500))
      const data: CachedData<T>[string] = JSON.parse(content)[key]

      if (data.validUntil) {
        if (new Date().getTime() >= data.validUntil) {
          return null
        }
      }

      return data.data
    } catch (error) {
      return null
    }
  }
  return { keys, put, get, scheduleTtl }
}

const accessCache = (folderName: string, config: Partial<CacheConfig> = {}): Promise<Cache> => {
  const isNode = typeof process !== 'undefined' && process.versions != null && process.versions.node != null
  if (isNode && process.env.NODE_ENV !== 'test') {
    return cache(folderName, config)
  }
  return Promise.resolve({
    keys: async () => [],
    get: async () => null,
    put: async () => {},
    scheduleTtl: () => {},
  })
}

export default accessCache
